import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import './ToolStyles.css';

const TimestampConverter = () => {
    const [currentTimestamp, setCurrentTimestamp] = useState(Date.now());
    const [timestampInput, setTimestampInput] = useState('');
    const [timestampUnit, setTimestampUnit] = useState<'ms' | 's' | 'us'>('ms');
    const [dateInput, setDateInput] = useState('');
    const [timestampResult, setTimestampResult] = useState('');
    const [dateResult, setDateResult] = useState('');

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTimestamp(Date.now());
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        const now = new Date();
        setDateInput(format(now, "yyyy-MM-dd'T'HH:mm:ss"));
    }, []);

    const convertTimestampToDate = () => {
        if (!timestampInput) {
            setDateResult('');
            return;
        }

        try {
            let timestamp = Number(timestampInput);

            // 根据单位转换为毫秒
            if (timestampUnit === 's') {
                timestamp = timestamp * 1000;
            } else if (timestampUnit === 'us') {
                timestamp = timestamp / 1000;
            }

            const date = new Date(timestamp);

            if (isNaN(date.getTime())) {
                setDateResult('无效的时间戳');
                return;
            }

            const formatted = format(date, 'yyyy-MM-dd HH:mm:ss');
            const iso = date.toISOString();
            const relative = getRelativeTime(date);

            setDateResult(`
标准格式: ${formatted}
ISO格式: ${iso}
相对时间: ${relative}
      `.trim());
        } catch {
            setDateResult('转换失败');
        }
    };

    const convertDateToTimestamp = () => {
        if (!dateInput) {
            setTimestampResult('');
            return;
        }

        try {
            const date = new Date(dateInput);

            if (isNaN(date.getTime())) {
                setTimestampResult('无效的日期时间');
                return;
            }

            const ms = date.getTime();
            const s = Math.floor(ms / 1000);
            const us = ms * 1000;

            setTimestampResult(`
毫秒 (ms): ${ms}
秒 (s): ${s}
微秒 (μs): ${us}
      `.trim());
        } catch {
            setTimestampResult('转换失败');
        }
    };

    const getRelativeTime = (date: Date): string => {
        const now = new Date();
        const diff = now.getTime() - date.getTime();
        const seconds = Math.floor(Math.abs(diff) / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        const prefix = diff > 0 ? '前' : '后';

        if (days > 0) return `${days}天${prefix}`;
        if (hours > 0) return `${hours}小时${prefix}`;
        if (minutes > 0) return `${minutes}分钟${prefix}`;
        return `${seconds}秒${prefix}`;
    };

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        convertTimestampToDate();
    }, [timestampInput, timestampUnit]);

    useEffect(() => {
        convertDateToTimestamp();
    }, [dateInput]);
    /* eslint-enable react-hooks/exhaustive-deps */

    return (
        <div className="tool-container">
            <div className="current-time-display">
                <h3>当前时间戳</h3>
                <div className="timestamp-grid">
                    <div className="timestamp-item">
                        <span className="timestamp-label">毫秒 (ms)</span>
                        <span className="timestamp-value">{currentTimestamp}</span>
                    </div>
                    <div className="timestamp-item">
                        <span className="timestamp-label">秒 (s)</span>
                        <span className="timestamp-value">{Math.floor(currentTimestamp / 1000)}</span>
                    </div>
                    <div className="timestamp-item">
                        <span className="timestamp-label">当前时间</span>
                        <span className="timestamp-value">
                            {format(new Date(currentTimestamp), 'yyyy-MM-dd HH:mm:ss')}
                        </span>
                    </div>
                </div>
            </div>

            <div className="converter-section">
                <h3>时间戳转日期</h3>

                <div className="input-group">
                    <label className="input-label">时间戳</label>
                    <div className="input-with-select">
                        <input
                            type="text"
                            className="tool-input flex-1"
                            value={timestampInput}
                            onChange={(e) => setTimestampInput(e.target.value)}
                            placeholder="输入时间戳"
                        />
                        <select
                            className="tool-select inline"
                            value={timestampUnit}
                            onChange={(e) => setTimestampUnit(e.target.value as 'ms' | 's' | 'us')}
                        >
                            <option value="ms">毫秒</option>
                            <option value="s">秒</option>
                            <option value="us">微秒</option>
                        </select>
                    </div>
                </div>

                {dateResult && (
                    <div className="output-group">
                        <label className="output-label">转换结果</label>
                        <pre className="tool-output">{dateResult}</pre>
                    </div>
                )}
            </div>

            <div className="converter-section">
                <h3>日期转时间戳</h3>

                <div className="input-group">
                    <label className="input-label">日期时间</label>
                    <input
                        type="datetime-local"
                        className="tool-input"
                        value={dateInput}
                        onChange={(e) => setDateInput(e.target.value)}
                    />
                </div>

                {timestampResult && (
                    <div className="output-group">
                        <label className="output-label">转换结果</label>
                        <pre className="tool-output">{timestampResult}</pre>
                    </div>
                )}
            </div>

            <div className="info-box">
                <h4>说明：</h4>
                <ul>
                    <li>支持毫秒、秒、微秒三种时间戳格式</li>
                    <li>实时显示当前时间戳</li>
                    <li>支持双向转换</li>
                    <li>显示相对时间（多久之前/之后）</li>
                </ul>
            </div>
        </div>
    );
};

export default TimestampConverter;
